import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { lightPalette, darkPalette } from '../theme/palette';
import { typography } from '../theme/typography';
import { components } from '../theme/components';
import { shadows } from '../theme/shadows';
import { useTranslation } from 'react-i18next';

// RTL languages
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  actualMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [mode, setMode] = useState<ThemeMode>(() => {
    try {
      const saved = localStorage.getItem('themeMode');
      return (saved as ThemeMode) || 'system';
    } catch {
      return 'system';
    }
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    return mediaQuery?.matches ?? false;
  });

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (!mediaQuery || !mediaQuery.addEventListener) {
      return;
    }
    
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => {
      if (mediaQuery && mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      }
    };
  }, []);

  // Calculate actual theme mode
  const actualMode: 'light' | 'dark' = 
    mode === 'system' 
      ? (systemPrefersDark ? 'dark' : 'light')
      : mode;

  // Save mode preference
  useEffect(() => {
    try {
      localStorage.setItem('themeMode', mode);
    } catch {
      // Ignore localStorage errors in tests
    }
    
    // Update document attribute for CSS targeting
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('data-theme', actualMode);
    }
  }, [mode, actualMode]);

  const toggleMode = () => {
    setMode((prevMode) => {
      if (prevMode === 'light') return 'dark';
      if (prevMode === 'dark') return 'system';
      return 'light';
    });
  };

  const setModeValue = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  // Save mode to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('themeMode', mode);
    } catch {
      // Ignore localStorage errors in tests
    }
  }, [mode]);

  // Determine if current language is RTL
  const isRTL = RTL_LANGUAGES.includes(i18n.language);
  
  // Create MUI theme based on mode and direction
  const theme = createTheme({
    direction: isRTL ? 'rtl' : 'ltr',
    palette: actualMode === 'dark' ? darkPalette : lightPalette,
    typography,
    components: components(actualMode),
    shadows: shadows(actualMode),
    shape: {
      borderRadius: 8,
    },
  });

  const value: ThemeContextType = {
    mode,
    actualMode,
    setMode: setModeValue,
    toggleMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};